import { InfoIcon, Loader2Icon } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { useMessageRateLimitCapacityQuery } from '@/modules/user-external-accounts/queries/useMessageRateLimitCapacityQuery';

interface LimitRowProps {
  label: string;
  remaining: number;
  max: number;
}

function LimitRow({ label, remaining, max }: LimitRowProps) {
  const safeMax = max > 0 ? max : 1;
  const usagePercent = Math.max(
    0,
    Math.min(100, ((safeMax - remaining) / safeMax) * 100),
  );
  const remainingPercent = 100 - usagePercent;
  const isExhausted = remaining === 0;
  const isLow = !isExhausted && remainingPercent < 20;

  const used = max - remaining;

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5">
      <span className="text-neutral-650 text-xs">{label}</span>
      <span
        className={cn(
          'text-right text-xs font-medium tabular-nums',
          isExhausted && 'text-destructive',
          isLow && 'text-semantic-540',
          !isExhausted && !isLow && 'text-neutral-900',
        )}
      >
        {used}{' '}
        <span className="text-neutral-650 font-normal">/ {max} used</span>
      </span>
      <div className="col-span-2 h-px overflow-hidden rounded-full bg-neutral-300">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            isExhausted && 'bg-destructive',
            isLow && 'bg-semantic-540',
            !isExhausted && !isLow && 'bg-primary',
          )}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={max - remaining}
          aria-label={`${label} usage`}
          style={{ width: `${usagePercent}%` }}
        />
      </div>
    </div>
  );
}

interface LinkedInLimitsProps {
  linkedInAccountId: string;
}

export function LinkedInLimits({ linkedInAccountId }: LinkedInLimitsProps) {
  const messageRateLimitQuery =
    useMessageRateLimitCapacityQuery(linkedInAccountId);

  if (messageRateLimitQuery.isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 border-t border-neutral-200 pt-3">
        <Loader2Icon className="h-3.5 w-3.5 animate-spin text-neutral-500" />
        <span className="text-neutral-650 text-xs">
          Loading messaging limits
        </span>
      </div>
    );
  }

  if (messageRateLimitQuery.isError || !messageRateLimitQuery.data) {
    return (
      <p className="text-destructive mt-3 border-t border-neutral-200 pt-3 text-xs">
        Failed to load messaging limits.
      </p>
    );
  }

  const {
    maxInvitationsPerDay,
    maxInvitationsPerWeek,
    maxMessagesPerDay,
    maxMessagesPerWeek,
    remainingInvitationsThisWeek,
    remainingInvitationsToday,
    remainingMessagesThisWeek,
    remainingMessagesToday,
  } = messageRateLimitQuery.data;

  return (
    <div className="mt-4 border-t border-neutral-200 pt-4">
      <div className="mb-4 flex items-center gap-1.5">
        <p className="text-neutral-650 text-xs font-medium tracking-wide uppercase">
          Limits
        </p>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon className="text-neutral-650 h-3.5 w-3.5 cursor-default" />
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-80">
            When a limit is reached, pending messages are queued and
            automatically sent once capacity resets. Capacity resets every
            minute.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <LimitRow
          label="Invites today"
          remaining={remainingInvitationsToday}
          max={maxInvitationsPerDay}
        />
        <LimitRow
          label="Invites this week"
          remaining={remainingInvitationsThisWeek}
          max={maxInvitationsPerWeek}
        />
        <LimitRow
          label="Messages today"
          remaining={remainingMessagesToday}
          max={maxMessagesPerDay}
        />
        <LimitRow
          label="Messages this week"
          remaining={remainingMessagesThisWeek}
          max={maxMessagesPerWeek}
        />
      </div>
    </div>
  );
}
